import { BehaviorSubject } from 'rxjs';

export type LoginSuccessCallback = () => void;

/**
 * App-wide, single-instance channel for the post-login callback.
 *
 * The login modal (root `@login/(.)login` slot) and the component that initiates
 * login (e.g. `LoginLink` on a page) live in different parallel-route slots, each
 * under its OWN `AuthProvider` instance. A per-provider React-context subject can
 * therefore never carry the callback across that slot boundary — the modal would
 * read `null` and fall back to a hard `window.location.href` reload, throwing away
 * the page (and any typed-in checkout card/contact).
 *
 * A module-level singleton is the natural cross-tree channel: there is only ever
 * one login modal open app-wide, and the callback is a transient one-shot handoff.
 * It is only ever written/read in the browser, so it holds no value during SSR
 * (no cross-request leakage).
 */
const loginSuccessCallback$ = new BehaviorSubject<LoginSuccessCallback | null>(
  null,
);

/** Register the callback the login modal should invoke on success. */
export function setLoginSuccessCallback(
  callback: LoginSuccessCallback | null,
): void {
  loginSuccessCallback$.next(callback);
}

/** Read and clear the registered callback (consumed by the login modal). */
export function consumeLoginSuccessCallback(): LoginSuccessCallback | null {
  const callback = loginSuccessCallback$.getValue();
  loginSuccessCallback$.next(null);
  return callback;
}
